import { Injectable } from '@angular/core';
import {
  ILoginUserEmployeeBindModel
} from '@blcloud/bl-datamodel';
import { LoginResourceService, PageId } from '@blcloud/bl-ng-share-module';
import { Observable } from 'rxjs/Observable';
import { map } from 'rxjs/operators/map';
import {
  filter as _filter, includes as _includes, forEach as _forEach, uniqBy as _uniqBy, cloneDeep as _cloneDeep, isEmpty as _isEmpty,
  isNil as _isNil
} from 'lodash';
import { BlConditionInfo, BlConditionType, BlListResponseBody } from '@blcloud/bl-ng-common';
import { IOutputProgressStatus } from '@blcloud/bl-datamodel';
import { OutputProgressStatusResource } from '@blcloud/bl-ng-resource';
import { BlDateFormatPipe } from '@blcloud/bl-ng-ui-component';
import { PrintPageId } from '../../shared/screen/print-page-id-define';
import { GridOptions } from 'ag-grid';
import { ValueGetterParams } from 'ag-grid/dist/lib/entities/colDef';
import { IReportOutputStatusSearchInfo, SearchConditionGroupDiv } from './report-output-status-define';
import { DateEra, DateSeparator, DateTimeOutput, DateTimePadding, DateTimeUtils } from '@blcloud/bl-common';
import { ManualAutoCreateDiv } from '@blcloud/bl-datamodel/enum/bizcmn/manual-auto-create-div';

/** 組織別仕入先元帳のページID */
const PM_ORGANIZATION_SUPPLIER_LEDGER_REPORT_PAGE_IDS = 'PmOrganizationSupplierLedger';

/**
 * 帳票出力状況画面サービス
 */
@Injectable()
export class ReportOutputStatusPageService {

  /**
   * grid設定値
   */
  gridOptions: GridOptions = {
    columnDefs: [
      {
        headerName: 'No.',
        width: 50,
        minWidth: 50,
        maxWidth: 50,
        valueGetter: (args: ValueGetterParams) => {
          return args.node.rowIndex + 1;
        },
        cellStyle: { 'justify-content': 'flex-end' },
        suppressSorting: true,
      },
      {
        headerName: '出力状況',
        field: 'outputProgressStatusDivEnumName',
        cellStyle: { 'justify-content': 'flex-start' },
        width: 130,
        minWidth: 130,
        maxWidth: 130,
      },
      {
        headerName: '出力名',
        field: 'reportLayoutFileName',
        cellStyle: { 'justify-content': 'flex-start' },
        width: 250,
        minWidth: 250,
        maxWidth: 300,
      },
      {
        headerName: '作成日時',
        field: 'reportCreateDateTime',
        cellStyle: { 'justify-content': 'flex-start' },
        width: 180,
        minWidth: 180,
        maxWidth: 180,
      },
      {
        headerName: '抽出条件',
        field: 'targetRange',
        cellStyle: { 'justify-content': 'flex-start' },
        minWidth: 300,

      },
    ],
    enableColResize: false,
    suppressMovableColumns: true
  };

  /**
   * コンストラクタ
   * @param loginResourceService  ログイン管理リソースサービス
   * @param outputProgressStatusResource 『出力進捗状況』リソースクラス
   */
  constructor(
    private loginResourceService: LoginResourceService,
    private outputProgressStatusResource: OutputProgressStatusResource
  ) {
  }

  /**
   * 画面表示時のログインユーザ情報取得
   */
  initializeOrganization(): Observable<ILoginUserEmployeeBindModel> {
    return this.loginResourceService.getLoginUserEmployeeBindModel();
  }

  /**
   * 出力状況APIを呼び出す。
   * @param searchInfo 呼び出し時のパラメータ
   */
  getOutputStatus(
    searchInfo: IReportOutputStatusSearchInfo, page: number, size: number
  ): Observable<BlListResponseBody<IOutputProgressStatus>> {
    let conditions: BlConditionInfo[][] = [];
    const conditionsG1 = this.getConditions(searchInfo, SearchConditionGroupDiv.G1);
    if (!_isEmpty(conditionsG1)) {
      // 画面項目(EL1-2-2)は「全て」または「発注一覧表」のみ場合、g2を設定
      if (searchInfo.pageId === 'all' || searchInfo.pageId === PageId.PmOrderList) {
        const conditionsG2 = this.getConditions(searchInfo, SearchConditionGroupDiv.G2);
        conditions = [conditionsG1, conditionsG2];
      } else {
        conditions = [conditionsG1];
      }
    }
    const sortFields = [
      { field: 'reportCreateDateTime', desc: true }
    ];


    return this.outputProgressStatusResource.search<IOutputProgressStatus>(
      {
        conditions,
        sortFields,
        page,
        size
      }
    ).pipe(
      map((response: BlListResponseBody<IOutputProgressStatus>) => {
        return this.formatReportCreateDateTime(_isNil(response) ? { totalCount: 0 } : response);
      })
    );
  }

  /**
   * 帳票ファイル作成日時のフォーマットを行う。
   * @param data 帳票出力API検索結果または出力状況API検索結果
   */
  private formatReportCreateDateTime(data: any): any {
    const tmpData = _cloneDeep(data);
    let allPageIds = new Array<string>();
    allPageIds = this.getAllPageIds();
    tmpData.searchResultList = _filter(tmpData.searchResultList, element =>
      _includes(allPageIds, element.pageId)
    ).map((element) => ({
      ...element,
      reportCreateDateTime: new BlDateFormatPipe().transform(element.reportCreateDateTime, 'YMD_LTS', '', '/', 'ad')
    }));

    return tmpData;

  }

  /**
   * 全機能のサブ画面IDを取得
   * @returns サブ画面ID配列
   */
  getAllPageIds(): Array<string> {
    let allPageIds = new Array<string>();
    _forEach(this.addReportNameComboBox(), (element) => {
      if (element.value !== 'all') {
        allPageIds = allPageIds.concat(this.getPageIds(element.value));
      }
    });
    return allPageIds;
  }

  /** 日付変換用
   * @param date 変換対象日付
   * @return 変換後日付文字列（YYYY-MM-DD）
   */
  formatDate(date: number): string {
    return DateTimeUtils.format(date, DateTimeOutput.YMD, DateTimePadding.ZERO, DateSeparator.HYPHEN, DateEra.AD);
  }

  /**
   * 帳票出力状況API呼び出し時の検索条件を(存在すれば)設定する。
   * @param searchInfo 呼び出し時のパラメータ
   * @param groupDiv 検索条件グループ区分
   */
  private getConditions(searchInfo: IReportOutputStatusSearchInfo, groupDiv: SearchConditionGroupDiv): BlConditionInfo[] {
    const conditions: BlConditionInfo[] = [];
    switch (groupDiv) {
      case SearchConditionGroupDiv.G1:
        conditions.push({ key: 'organizationCode', value: searchInfo.organizationCode, type: BlConditionType.Equal });
        conditions.push({ key: 'employeeCode', value: searchInfo.employeeCode, type: BlConditionType.Equal });
        break;
      case SearchConditionGroupDiv.G2:
        conditions.push({ key: 'notificationEmployeeCodeList', value: [searchInfo.employeeCode], type: BlConditionType.In });
        conditions.push({ key: 'orderManualAutoCreateDiv', value: ManualAutoCreateDiv.Auto, type: BlConditionType.Equal });
        break;
    }
    if (!_isEmpty(searchInfo.pageId) && searchInfo.pageId !== 'all') {
      conditions.push({ key: 'pageId', value: searchInfo.pageIds, type: BlConditionType.In });
    }
    if (!_isEmpty(searchInfo.searchDateStart) && searchInfo.searchDateStart !== '1900-01-01') {
      conditions.push({ key: 'reportCreateDateTime', value: searchInfo.searchDateStart, type: BlConditionType.GreaterThanEqual });
    }
    if (!_isEmpty(searchInfo.searchDateEnd) && searchInfo.searchDateEnd !== '1900-01-01') {
      const searchDateEnd = this.formatDate(DateTimeUtils.addDays(DateTimeUtils.parseDate(searchInfo.searchDateEnd), 1));
      conditions.push({ key: 'reportCreateDateTime', value: searchDateEnd, type: BlConditionType.LessThanEqual });
    }
    if (searchInfo.outputStatusDiv !== 'all') {
      conditions.push({ key: 'outputProgressStatusDiv', value: searchInfo.outputStatusDiv, type: BlConditionType.Equal });
    }

    return conditions;
  }

  /**
   * 機能名コンボボックス値のセット
   * @returns
   */
  addReportNameComboBox(): Array<{ value: string; text: string; }> {
    const reportNameDivArray = new Array<{ value: string; text: string; }>();

    reportNameDivArray.push({ value: 'all', text: '全て' });
    reportNameDivArray.push({ value: PageId.DepositReportPm, text: '入金伝票一覧表' });
    reportNameDivArray.push({ value: PageId.BillingListPm, text: '請求一覧表' });
    reportNameDivArray.push({ value: PageId.ArListPm, text: '売掛金一覧表' });
    reportNameDivArray.push({ value: PageId.NotPaymentList, text: '未入金一覧表' });
    reportNameDivArray.push({ value: PageId.ConsolidatedPayList, text: '支払一覧表' });
    reportNameDivArray.push({ value: PageId.PmSalesPurchaseComparisonInfo, text: '売上仕入対比表' });
    reportNameDivArray.push({ value: PageId.PmPurchaseConfirmationList, text: '仕入確認表' });
    reportNameDivArray.push({ value: PageId.PmPurchaseDailyReportMonthlyReport, text: '仕入日報月報' });
    reportNameDivArray.push({ value: PageId.PmOrderList, text: '発注一覧表' });
    reportNameDivArray.push({ value: PageId.PmOtherOrderDetailList, text: '発注書' });
    reportNameDivArray.push({ value: PageId.PmPurchaseSalesPerformanceList, text: '仕入売上実績表' });
    reportNameDivArray.push({ value: PageId.PmOrderLoanConfirmation, text: '受注貸出確認表' });
    reportNameDivArray.push({ value: PageId.PmEstimateConfirmation, text: '見積確認表' });
    reportNameDivArray.push({ value: PageId.PmCustomerPreviousStatisticsList, text: '得意先過年度統計表' });
    reportNameDivArray.push({ value: PageId.PmSalesReport, text: '売上日報月報' });
    reportNameDivArray.push({ value: PageId.PmSalesConfirmation, text: '売上確認表' });
    reportNameDivArray.push({ value: PageId.PmSalesMonthlyAnnualReport, text: '返品理由分析表' });
    reportNameDivArray.push({ value: PageId.PmYearOnYearList, text: '前年対比表' });
    reportNameDivArray.push({ value: PageId.PmSalesAnnualReport, text: '売上月報年報' });
    reportNameDivArray.push({ value: PageId.PmSalesPerformanceList, text: '売上実績表' });
    reportNameDivArray.push({ value: PageId.PmStockMoveConfirmation, text: '在庫移動確認表' });
    reportNameDivArray.push({ value: PageId.PmStockAdjustConfirmList, text: '在庫調整確認表' });
    reportNameDivArray.push({ value: PageId.PmStockReceivingShippingConfirmation, text: '在庫入出庫確認表' });
    reportNameDivArray.push({ value: PageId.PmStockReceivingShippingList, text: '在庫入出荷一覧表' });
    reportNameDivArray.push({ value: PageId.PmStockList, text: '在庫売上一覧表' });
    reportNameDivArray.push({ value: PageId.PmStockSignPrint, text: '在庫看板印刷' });
    reportNameDivArray.push({ value: PageId.PmStockMonthlyAnnualReport, text: '在庫月報年報' });
    reportNameDivArray.push({ value: PageId.PmStockUnshippedList, text: '在庫未出荷一覧表' });
    reportNameDivArray.push({ value: PageId.PmVehicleShipmentPerformanceList, text: '車両別出荷実績表' });
    reportNameDivArray.push({ value: PageId.PmVehicleModelShipmentList, text: '型式別出荷対応表' });
    reportNameDivArray.push({ value: PageId.PmCustomerElectronicLedger, text: '得意先電子元帳' });
    reportNameDivArray.push({ value: PageId.PmPurchaseChecklist, text: '仕入チェックリスト' });
    reportNameDivArray.push({ value: PageId.PmStockTakingSearchInfo, text: '棚卸差異表' });
    reportNameDivArray.push({ value: PageId.AccPayListInfo, text: '仕入先元帳' });
    reportNameDivArray.push({ value: PageId.PmInventoriesAggregateList, text: '棚卸表' });
    reportNameDivArray.push({ value: PageId.RateMaster, text: '掛率設定一覧表' });
    reportNameDivArray.push({ value: PageId.CustmerSalesDistributionReport, text: '取引分布表' });
    reportNameDivArray.push({ value: PageId.PmSalesUnmatchConfList, text: '売上不整合確認表' });
    reportNameDivArray.push({ value: PageId.PmPurchaseUnmatchConfList, text: '仕入不整合確認表' });
    reportNameDivArray.push({ value: PageId.PmStockOverList, text: '過剰在庫一覧表' });
    reportNameDivArray.push({ value: PageId.PmStockAnalysisOrderList, text: '在庫分析順位表' });
    reportNameDivArray.push({ value: PageId.PmStockSettingListPrint, text: '在庫設定一覧印刷' });
    reportNameDivArray.push({ value: PageId.PmPurchaseAnalysisList, text: '仕入分析表' });
    reportNameDivArray.push({ value: PageId.PmSalesPurchaseComparisonAnnual, text: '売上仕入対比表（月報年報）' });
    reportNameDivArray.push({ value: PageId.PmShippingGoodsAnalysisList, text: '出荷商品分析表' });
    reportNameDivArray.push({ value: PageId.PmSalesImpItemMngPerfInquiry, text: '重点品目実績表' });
    reportNameDivArray.push({ value: PageId.PmSalesImpItemMngPerfTransition, text: '重点品目推移表' });
    reportNameDivArray.push({ value: PageId.PmStockReplenishment, text: '委託在庫補充一覧表' });
    reportNameDivArray.push({ value: PageId.PmStockCollectionList, text: '棚卸集計表' });
    reportNameDivArray.push({ value: PageId.PmOrganizationAccPayListInfo, text: '組織別買掛残高一覧表' });
    reportNameDivArray.push({ value: PageId.PmOrganizationConsolidatedPayList, text: '組織別支払一覧表' });
    reportNameDivArray.push({ value: PM_ORGANIZATION_SUPPLIER_LEDGER_REPORT_PAGE_IDS, text: '組織別仕入先元帳' });
    reportNameDivArray.push({ value: PageId.PmStockReplenishmentRequestList, text: '在庫補充依頼一覧表' });
    reportNameDivArray.push({ value: PageId.PmInventoriesResearchList, text: '棚卸調査表' });
    return reportNameDivArray;
  }

  /**
   * 機能ごとのサブ画面IDの取得
   * @param pageId 機能ごとのメイン画面ID
   * @returns サブ画面ID配列
   */
  getPageIds(pageId: string): Array<string> {
    const pageIds = new Array<string>();
    switch (pageId) {
      // 入金伝票一覧表
      case PageId.DepositReportPm:
        pageIds.push(PageId.DepositReportPm + '-1');
        break;
      // 請求書一覧表
      case PageId.BillingListPm:
        // 請求書一覧表（計上月範囲指定あり_部品商）
        pageIds.push(PageId.BillingListPm + '-03');
        // 請求書一覧表（計上月範囲指定なし_部品商）
        pageIds.push(PageId.BillingListPm + '-04');
        break;
      // 売掛金一覧表
      case PageId.ArListPm:
        // 売掛金一覧表（計上月範囲指定なし_部品商）
        pageIds.push(PageId.ArListPm + '-2');
        // 売掛金一覧表（計上月範囲指定あり_部品商）
        pageIds.push(PageId.ArListPm + '-4');
        // 売掛金一覧表（金種計_部品商）
        pageIds.push(PageId.ArListPm + '-3');
        // 売掛金一覧表（計上月範囲指定金種計部品商）
        pageIds.push(PageId.ArListPm + '-5');
        break;
      // 未入金一覧表
      case PageId.NotPaymentList:
        pageIds.push(PageId.NotPaymentList);
        break;
      // 受注貸出確認表
      case PageId.PmOrderLoanConfirmation:
        // 受注確認表(合計)
        pageIds.push(PrintPageId.PmOrderLoanConfirmation_Sales_0280.toString());
        // 受注確認表(明細)
        pageIds.push(PrintPageId.PmOrderLoanConfirmation_Sales_0281.toString());
        // 貸出確認表(合計)
        pageIds.push(PrintPageId.PmOrderLoanConfirmation_Sales_0282.toString());
        // 貸出確認表(明細)
        pageIds.push(PrintPageId.PmOrderLoanConfirmation_Sales_0283.toString());
        // 結合テスト用 >>>
        // 受注確認表(合計)
        pageIds.push(PageId.PmOrderLoanConfirmation + '-Pm-02');
        // 受注確認表(明細)
        pageIds.push(PageId.PmOrderLoanConfirmation + '-Pm-03');
        // 貸出確認表(合計)
        pageIds.push(PageId.PmOrderLoanConfirmation + '-Pm-12');
        // 貸出確認表(明細)
        pageIds.push(PageId.PmOrderLoanConfirmation + '-Pm-13');
        // 結合テスト用 <<<
        break;
      // 売上確認表
      case PageId.PmSalesConfirmation:
        // 売上確認表(伝票タイプ)
        pageIds.push(PrintPageId.PmSalesConfirmation_Sales_0270.toString());
        // 売上確認表(明細タイプ)
        pageIds.push(PrintPageId.PmSalesConfirmation_Sales_0271.toString());
        // 新旧帳票比較用 >>>
        // 売上確認表(伝票タイプ)
        pageIds.push(PageId.PmSalesConfirmation + '-Pm-2');
        // 売上確認表(明細タイプ)
        pageIds.push(PageId.PmSalesConfirmation + '-Pm-3');
        // 新旧帳票比較用 <<<
        break;
      // 売上日報月報
      case PageId.PmSalesReport:
        pageIds.push(PrintPageId.PmSalesReport_0_0_0.toString());
        pageIds.push(PrintPageId.PmSalesReport_0_0_1.toString());
        pageIds.push(PrintPageId.PmSalesReport_0_0_2.toString());
        pageIds.push(PrintPageId.PmSalesReport_0_0_3.toString());
        pageIds.push(PageId.PmSalesReport + '-Pm-004');
        pageIds.push(PrintPageId.PmSalesReport_0_1_0.toString());
        pageIds.push(PrintPageId.PmSalesReport_0_1_1.toString());
        pageIds.push(PrintPageId.PmSalesReport_0_1_2.toString());
        pageIds.push(PrintPageId.PmSalesReport_0_1_3.toString());
        pageIds.push(PageId.PmSalesReport + '-Pm-014');
        pageIds.push(PrintPageId.PmSalesReport_1_0_0.toString());
        pageIds.push(PrintPageId.PmSalesReport_1_0_1.toString());
        pageIds.push(PrintPageId.PmSalesReport_1_0_2.toString());
        pageIds.push(PrintPageId.PmSalesReport_1_0_3.toString());
        pageIds.push(PrintPageId.PmSalesReport_1_1_0.toString());
        pageIds.push(PrintPageId.PmSalesReport_1_1_1.toString());
        pageIds.push(PrintPageId.PmSalesReport_1_1_2.toString());
        pageIds.push(PrintPageId.PmSalesReport_1_1_3.toString());
        pageIds.push(PrintPageId.PmSalesReport_2_0_0.toString());
        pageIds.push(PrintPageId.PmSalesReport_2_0_1.toString());
        pageIds.push(PrintPageId.PmSalesReport_2_0_2.toString());
        pageIds.push(PrintPageId.PmSalesReport_2_0_3.toString());
        pageIds.push(PrintPageId.PmSalesReport_2_1_0.toString());
        pageIds.push(PrintPageId.PmSalesReport_2_1_1.toString());
        pageIds.push(PrintPageId.PmSalesReport_2_1_2.toString());
        pageIds.push(PrintPageId.PmSalesReport_2_1_3.toString());
        pageIds.push(PrintPageId.PmSalesReport_3_0_0.toString());
        pageIds.push(PrintPageId.PmSalesReport_3_0_1.toString());
        pageIds.push(PrintPageId.PmSalesReport_3_0_2.toString());
        pageIds.push(PrintPageId.PmSalesReport_3_0_3.toString());
        pageIds.push(PrintPageId.PmSalesReport_3_1_0.toString());
        pageIds.push(PrintPageId.PmSalesReport_3_1_1.toString());
        pageIds.push(PrintPageId.PmSalesReport_3_1_2.toString());
        pageIds.push(PrintPageId.PmSalesReport_3_1_3.toString());
        pageIds.push(PrintPageId.PmSalesReport_4_0_0.toString());
        pageIds.push(PrintPageId.PmSalesReport_4_0_1.toString());
        pageIds.push(PrintPageId.PmSalesReport_4_0_2.toString());
        pageIds.push(PrintPageId.PmSalesReport_4_1_0.toString());
        pageIds.push(PrintPageId.PmSalesReport_4_1_1.toString());
        pageIds.push(PrintPageId.PmSalesReport_4_1_2.toString());
        pageIds.push(PrintPageId.PmSalesReport_5_0_0.toString());
        pageIds.push(PrintPageId.PmSalesReport_5_0_1.toString());
        pageIds.push(PrintPageId.PmSalesReport_5_0_2.toString());
        pageIds.push(PrintPageId.PmSalesReport_5_1_0.toString());
        pageIds.push(PrintPageId.PmSalesReport_5_1_1.toString());
        pageIds.push(PrintPageId.PmSalesReport_5_1_2.toString());
        pageIds.push(PrintPageId.PmSalesReport_6_0.toString());
        pageIds.push(PrintPageId.PmSalesReport_6_1.toString());
        // 新旧帳票比較用 >>>
        // 売上日報月報(得意先別)
        pageIds.push(PageId.PmSalesReport + '-Pm-008');
        pageIds.push(PageId.PmSalesReport + '-Pm-005');
        pageIds.push(PageId.PmSalesReport + '-Pm-006');
        pageIds.push(PageId.PmSalesReport + '-Pm-005');
        pageIds.push(PageId.PmSalesReport + '-Pm-007');
        pageIds.push(PageId.PmSalesReport + '-Pm-009');
        pageIds.push(PageId.PmSalesReport + '-Pm-005');
        pageIds.push(PageId.PmSalesReport + '-Pm-018');
        pageIds.push(PageId.PmSalesReport + '-Pm-016');
        pageIds.push(PageId.PmSalesReport + '-Pm-017');
        pageIds.push(PageId.PmSalesReport + '-Pm-019');
        // 売上日報月報(担当者別)
        pageIds.push(PageId.PmSalesReport + '-Pm-104');
        pageIds.push(PageId.PmSalesReport + '-Pm-105');
        pageIds.push(PageId.PmSalesReport + '-Pm-106');
        pageIds.push(PageId.PmSalesReport + '-Pm-107');
        pageIds.push(PageId.PmSalesReport + '-Pm-114');
        pageIds.push(PageId.PmSalesReport + '-Pm-115');
        pageIds.push(PageId.PmSalesReport + '-Pm-116');
        pageIds.push(PageId.PmSalesReport + '-Pm-117');
        // 売上日報月報(受注者別)
        pageIds.push(PageId.PmSalesReport + '-Pm-204');
        pageIds.push(PageId.PmSalesReport + '-Pm-205');
        pageIds.push(PageId.PmSalesReport + '-Pm-206');
        pageIds.push(PageId.PmSalesReport + '-Pm-207');
        pageIds.push(PageId.PmSalesReport + '-Pm-214');
        pageIds.push(PageId.PmSalesReport + '-Pm-215');
        pageIds.push(PageId.PmSalesReport + '-Pm-216');
        pageIds.push(PageId.PmSalesReport + '-Pm-217');
        // 売上日報月報(発行者別)
        pageIds.push(PageId.PmSalesReport + '-Pm-305');
        pageIds.push(PageId.PmSalesReport + '-Pm-306');
        pageIds.push(PageId.PmSalesReport + '-Pm-307');
        pageIds.push(PageId.PmSalesReport + '-Pm-308');
        pageIds.push(PageId.PmSalesReport + '-Pm-315');
        pageIds.push(PageId.PmSalesReport + '-Pm-316');
        pageIds.push(PageId.PmSalesReport + '-Pm-317');
        pageIds.push(PageId.PmSalesReport + '-Pm-318');
        // 売上日報月報(地区別)
        pageIds.push(PageId.PmSalesReport + '-Pm-403');
        pageIds.push(PageId.PmSalesReport + '-Pm-404');
        pageIds.push(PageId.PmSalesReport + '-Pm-405');
        pageIds.push(PageId.PmSalesReport + '-Pm-413');
        pageIds.push(PageId.PmSalesReport + '-Pm-414');
        pageIds.push(PageId.PmSalesReport + '-Pm-415');
        // 売上日報月報(業種別)
        pageIds.push(PageId.PmSalesReport + '-Pm-503');
        pageIds.push(PageId.PmSalesReport + '-Pm-504');
        pageIds.push(PageId.PmSalesReport + '-Pm-505');
        pageIds.push(PageId.PmSalesReport + '-Pm-513');
        pageIds.push(PageId.PmSalesReport + '-Pm-514');
        pageIds.push(PageId.PmSalesReport + '-Pm-515');
        // 売上日報月報(販売区分別)
        pageIds.push(PageId.PmSalesReport + '-Pm-62');
        pageIds.push(PageId.PmSalesReport + '-Pm-63');
        // 新旧帳票比較用 <<<
        break;
      // 在庫移動確認表
      case PageId.PmStockMoveConfirmation:
        // 在庫移動確認表
        pageIds.push(PrintPageId.PmStockMoveConfirmation_Stock_0020.toString());
        // 在庫移動確認表（集計）
        pageIds.push(PrintPageId.PmStockMoveConfirmation_Stock_0021.toString());
        // 新旧帳票比較用 >>>
        // 在庫移動確認表
        pageIds.push(PageId.PmStockMoveConfirmation + '-Pm-2');
        // 在庫移動確認表（集計）
        pageIds.push(PageId.PmStockMoveConfirmation + '-Pm-3');
        // 新旧帳票比較用 <<<
        break;
      // 仕入確認表
      case PageId.PmPurchaseConfirmationList:
        // 仕入確認表（明細タイプ）
        pageIds.push(PrintPageId.PmPurchaseConfirmationList_Purchase_0050.toString());
        // 仕入確認表（伝票タイプ）
        pageIds.push(PrintPageId.PmPurchaseConfirmationList_Purchase_0051.toString());
        // 新旧帳票比較用 >>>
        // 仕入確認表（明細タイプ）
        pageIds.push('G-Pm-Purchase-0130-Pm-3');
        // 仕入確認表（伝票タイプ）
        pageIds.push('G-Pm-Purchase-0130-Pm-2');
        // 新旧帳票比較用 <<<
        break;
      // 車両別出荷実績表
      case PageId.PmVehicleShipmentPerformanceList:
        // 車両別出荷実績表
        pageIds.push(PageId.PmVehicleShipmentPerformanceList + '-Pm-0');
        // 新旧帳票比較用 >>>
        // 車両別出荷実績表
        pageIds.push(PageId.PmVehicleShipmentPerformanceList + '-Pm-2');
        // 新旧帳票比較用 <<<
        break;
      // 発注一覧表
      case PageId.PmOrderList:
        // 発注一覧表
        pageIds.push(PrintPageId.PmOrderList_Purchase_0030.toString());
        // 発注残一覧表
        pageIds.push(PrintPageId.PmOrderList_Purchase_0031.toString());
        // 新旧帳票比較用 >>>
        // 発注一覧表
        pageIds.push(PageId.PmOrderList + '-Pm-2');
        // 発注残一覧表
        pageIds.push(PageId.PmOrderList + '-Pm-3');
        // 新旧帳票比較用 <<<
        break;
      // 発注書
      case PageId.PmOtherOrderDetailList:
        // 発注書
        pageIds.push(PageId.PmOtherOrderDetailList.toString());
        break;
      // 仕入日報月報
      case PageId.PmPurchaseDailyReportMonthlyReport:
        // 仕入日報月報（組織別）
        pageIds.push(PrintPageId.PmPurchaseDailyReportMonthlyReport_Purchase_0080.toString());
        // 仕入日報月報（仕入先別）
        pageIds.push(PrintPageId.PmPurchaseDailyReportMonthlyReport_Purchase_0081.toString());
        // 新旧帳票比較用 >>>
        // 仕入日報月報（組織別）
        pageIds.push('G-Pm-Purchase-0140-Pm-2');
        // 仕入日報月報（仕入先別）
        pageIds.push('G-Pm-Purchase-0140-Pm-3');
        // 新旧帳票比較用 <<<
        break;
      // 得意先過年度統計表
      case PageId.PmCustomerPreviousStatisticsList:
        // 得意先過年度統計表（得意先別）
        pageIds.push(PrintPageId.PmCustomerPreviousStatisticsList_Sales_0131_1.toString());
        // 得意先過年度統計表（組織別）
        pageIds.push(PrintPageId.PmCustomerPreviousStatisticsList_Sales_0130_1.toString());
        // 得意先過年度統計表（得意先組織別）
        pageIds.push(PrintPageId.PmCustomerPreviousStatisticsList_Sales_0132.toString());
        // 得意先過年度統計表（管理組織別）
        pageIds.push(PrintPageId.PmCustomerPreviousStatisticsList_Sales_0130_2.toString());
        // 得意先過年度統計表（請求先別）
        pageIds.push(PrintPageId.PmCustomerPreviousStatisticsList_Sales_0131_2.toString());
        // 得意先過年度統計表（担当者得意先別）
        pageIds.push(PrintPageId.PmCustomerPreviousStatisticsList_Sales_0133.toString());

        // 新旧帳票比較用 >>>
        // 得意先過年度統計表（得意先別）
        pageIds.push(PageId.PmCustomerPreviousStatisticsList + '-Pm-5');
        // 得意先過年度統計表（得意先組織別）
        pageIds.push(PageId.PmCustomerPreviousStatisticsList + '-Pm-7');
        // 得意先過年度統計表（管理組織別）
        pageIds.push(PageId.PmCustomerPreviousStatisticsList + '-Pm-8');
        // 得意先過年度統計表（請求先別）
        pageIds.push(PageId.PmCustomerPreviousStatisticsList + '-Pm-9');
        // 新旧帳票比較用 <<<
        break;
      // 返品理由分析表
      case PageId.PmSalesMonthlyAnnualReport:
        // 返品理由分析表（返品理由別）
        pageIds.push(PrintPageId.PmSalesMonthlyAnnualReport_Sales_0120.toString());
        // 返品理由分析表（得意先別）
        pageIds.push(PrintPageId.PmSalesMonthlyAnnualReport_Sales_0121.toString());
        // 返品理由分析表（担当者別）
        pageIds.push(PrintPageId.PmSalesMonthlyAnnualReport_Sales_0122.toString());
        // 返品理由分析表（受注者別）
        pageIds.push(PrintPageId.PmSalesMonthlyAnnualReport_Sales_0123.toString());
        // 返品理由分析表（発行者別）
        pageIds.push(PrintPageId.PmSalesMonthlyAnnualReport_Sales_0124.toString());
        // 新旧帳票比較用 >>>
        // 返品理由分析表（返品理由別）
        pageIds.push(PageId.PmSalesMonthlyAnnualReport + '-Pm-5');
        // 返品理由分析表（得意先別）
        pageIds.push(PageId.PmSalesMonthlyAnnualReport + '-Pm-6');
        // 返品理由分析表（担当者別）
        pageIds.push(PageId.PmSalesMonthlyAnnualReport + '-Pm-7');
        // 返品理由分析表（受注者別）
        pageIds.push(PageId.PmSalesMonthlyAnnualReport + '-Pm-8');
        // 返品理由分析表（発行者別）
        pageIds.push(PageId.PmSalesMonthlyAnnualReport + '-Pm-9');
        // 新旧帳票比較用 <<<
        break;
      // 在庫看板印刷
      case PageId.PmStockSignPrint:
        // 在庫看板(5×9) 連帳
        pageIds.push(PrintPageId.PmStockSignPrint_Stock_0150.toString());
        // 在庫看板(3×9) 連帳
        pageIds.push(PrintPageId.PmStockSignPrint_Stock_0151.toString());
        // 在庫看板(3×9) A4
        pageIds.push(PrintPageId.PmStockSignPrint_Stock_0153.toString());
        // 在庫看板(4×11) A4
        pageIds.push(PrintPageId.PmStockSignPrint_Stock_0155.toString());
        // 新旧帳票比較用 >>>
        pageIds.push(PageId.PmStockSignPrint + '-Pm-4');
        pageIds.push(PageId.PmStockSignPrint + '-Pm-5');
        pageIds.push(PageId.PmStockSignPrint + '-Pm-6');
        pageIds.push(PageId.PmStockSignPrint + '-Pm-7');
        // 新旧帳票比較用 <<<
        break;
      // 得意先電子元帳
      case PageId.PmCustomerElectronicLedger:
        // 得意先電子元帳
        pageIds.push(PageId.PmCustomerElectronicLedger + '-Pm');
        // 新旧帳票比較用 >>>
        // 得意先電子元帳
        pageIds.push(PageId.PmCustomerElectronicLedger + '-Pm-1');
        // 新旧帳票比較用 <<<
        break;
      // 仕入チェックリスト
      case PageId.PmPurchaseChecklist:
        // 仕入チェックリスト
        pageIds.push('G-Pm-Purchase-0040');
        // 新旧帳票比較用 >>>
        // 仕入チェックリスト
        pageIds.push('G-Pm-Purchase-0041');
        // 新旧帳票比較用 <<<
        break;
      // 棚卸差異表
      case PageId.PmStockTakingSearchInfo:
        // 棚卸差異表
        pageIds.push('G-Pm-Stock-0340');
        // 新旧帳票比較用 >>>
        // 棚卸差異表
        pageIds.push('G-Pm-Stock-0341');
        // 新旧帳票比較用 <<<
        break;
      // 在庫未出荷一覧表
      case PageId.PmStockUnshippedList:
        // 在庫未出荷一覧表（仕入先別）
        pageIds.push(PrintPageId.PmStockUnshippedList_Stock_0130.toString());
        // 在庫未出荷一覧表（棚番別）
        pageIds.push(PrintPageId.PmStockUnshippedList_Stock_0131.toString());
        // 在庫未出荷一覧表（BLコード別）
        pageIds.push(PrintPageId.PmStockUnshippedList_Stock_0132.toString());
        // 新旧帳票比較用 >>>
        // 在庫未出荷一覧表（仕入先別）
        pageIds.push(PageId.PmStockUnshippedList + '-Pm-2');
        // 在庫未出荷一覧表（棚番別）
        pageIds.push(PageId.PmStockUnshippedList + '-Pm-3');
        // 新旧帳票比較用 <<<
        break;
      // 見積確認表
      case PageId.PmEstimateConfirmation:
        // 見積確認表
        pageIds.push('G-Pm-Sales-0060');
        // 新旧帳票比較用 >>>
        // 見積確認表
        pageIds.push('G-Pm-Sales-0061');
        // 新旧帳票比較用 <<<
        break;
      // 仕入売上実績表
      case PageId.PmPurchaseSalesPerformanceList:
        // 仕入売上実績表
        pageIds.push(PageId.PmPurchaseSalesPerformanceList.toString());
        // 新旧帳票比較用 >>>
        // 仕入売上実績表
        pageIds.push('G-Pm-Purchase-0281');
        // 新旧帳票比較用 <<<
        break;
      // 在庫月報年報
      case PageId.PmStockMonthlyAnnualReport:
        // 在庫月報年報
        pageIds.push('G-Pm-Stock-0260');
        // 新旧帳票比較用 >>>
        // 在庫月報年報
        pageIds.push('G-Pm-Stock-0261');
        // 新旧帳票比較用 <<<
        break;
      // 前年対比表
      case PageId.PmYearOnYearList:
        // 前年対比表
        pageIds.push(PageId.PmYearOnYearList.toString());
        // 新旧帳票比較用 >>>
        // 前年対比表
        pageIds.push('G-Pm-Sales-0171');
        // 新旧帳票比較用 <<<
        break;
      // 在庫調整確認表
      case PageId.PmStockAdjustConfirmList:
        // 在庫調整確認表
        pageIds.push('G-Pm-Stock-0170');
        // 新旧帳票比較用 >>>
        // 在庫調整確認表
        pageIds.push('G-Pm-Stock-0171');
        // 新旧帳票比較用 <<<
        break;
      // 売上実績表
      case PageId.PmSalesPerformanceList:
        // 売上実績表（得意先別）
        pageIds.push('G-Pm-Sales-0190');
        // 新旧帳票比較用 >>>
        // 売上実績表（得意先別）
        pageIds.push('G-Pm-Sales-0191');
        // 新旧帳票比較用 <<<
        break;
      // 売上月報年報（得意先別）
      case PageId.PmSalesAnnualReport:
        // 売上月報年報（得意先別）
        pageIds.push('G-Pm-Sales-0180');
        // 新旧帳票比較用 >>>
        // 売上月報年報（得意先別）
        pageIds.push('G-Pm-Sales-0181');
        // 新旧帳票比較用 <<<
        break;
      // 在庫入出荷一覧表
      case PageId.PmStockReceivingShippingList:
        // 在庫入出荷一覧表
        pageIds.push('G-Pm-Stock-0200');
        // 新旧帳票比較用 >>>
        // 在庫入出荷一覧表
        pageIds.push('G-Pm-Stock-0201');
        // 新旧帳票比較用 <<<
        break;
      // 仕入先元帳
      case PageId.AccPayListInfo:
        // 仕入先元帳
        pageIds.push(PageId.AccPayListInfo + '-Pm');
        // 新旧帳票比較用 >>>
        // 仕入先元帳
        pageIds.push(PageId.AccPayListInfo + '-Pm-2');
        // 新旧帳票比較用 <<<
        break;
      // 在庫一覧表
      case PageId.PmStockList:
        // 在庫一覧表
        pageIds.push('G-Pm-Stock-0210');
        // 新旧帳票比較用 >>>
        // 在庫一覧表
        pageIds.push('G-Pm-Stock-0211');
        // 新旧帳票比較用 <<<
        break;
      // 在庫入出庫確認表
      case PageId.PmStockReceivingShippingConfirmation:
        // 在庫入出庫確認表
        pageIds.push('G-Pm-Stock-0190');
        // 新旧帳票比較用 >>>
        // 在庫入出庫確認表
        pageIds.push('G-Pm-Stock-0191');
        // 新旧帳票比較用 <<<
        break;
      // 売上仕入対比表
      case PageId.PmSalesPurchaseComparisonInfo:
        // 売上仕入対比表
        pageIds.push('G-Pm-Bizcmn-0120');
        // 新旧帳票比較用 >>>
        // 売上仕入対比表
        pageIds.push('G-Pm-Bizcmn-0121');
        // 新旧帳票比較用 <<<
        break;
      // 型式別出荷対応表
      case PageId.PmVehicleModelShipmentList:
        // 型式別出荷対応表
        pageIds.push('G-Pm-Vehicle-0030');
        // 新旧帳票比較用 >>>
        // 型式別出荷対応表
        pageIds.push('G-Pm-Vehicle-0031');
        // 新旧帳票比較用 <<<
        break;
      // 棚卸表
      case PageId.PmInventoriesAggregateList:
        // 棚卸表
        pageIds.push('G-Pm-Stock-0360');
        // 新旧帳票比較用 >>>
        // 棚卸表
        pageIds.push('G-Pm-Stock-0361');
        // 新旧帳票比較用 <<<
        break;
      // 支払一覧表
      case PageId.ConsolidatedPayList:
        // 「支払締月範囲指定区分」にチェック無し
        pageIds.push('G-Debt-0030-Pm-0');
        // 「支払締月範囲指定区分」にチェック有り
        pageIds.push('G-Debt-0030-Pm-1');
        // 新旧帳票比較用 >>>
        // 「支払締月範囲指定区分」にチェック無し
        pageIds.push('G-Debt-0030-Pm-4');
        // 「支払締月範囲指定区分」にチェック有り
        pageIds.push('G-Debt-0030-Pm-5');
        // 新旧帳票比較用 <<<
        break;
      // 委託在庫補充一覧表
      case PageId.PmStockReplenishment:
        // 委託在庫補充一覧表
        pageIds.push(PageId.PmStockReplenishment);
        break;
      // 組織別買掛残高一覧表
      case PageId.PmOrganizationAccPayListInfo:
        pageIds.push(PageId.PmOrganizationAccPayListInfo + '-Pm-1');
        pageIds.push(PageId.PmOrganizationAccPayListInfo + '-Pm-2');
        break;
      // 組織別支払一覧表
      case PageId.PmOrganizationConsolidatedPayList:
        pageIds.push(PageId.PmOrganizationConsolidatedPayList + '-Pm-1');
        pageIds.push(PageId.PmOrganizationConsolidatedPayList + '-Pm-2');
        break;
      // 組織別仕入先元帳
      case PM_ORGANIZATION_SUPPLIER_LEDGER_REPORT_PAGE_IDS:
        pageIds.push(PageId.PmOrganizationAccPayListInfo + '-Pm-3');
        pageIds.push(PageId.PmOrganizationConsolidatedPayList + '-Pm-3');
        break;
      // 在庫補充依頼一覧表
      case PageId.PmStockReplenishmentRequestList:
        pageIds.push(PageId.PmStockReplenishmentRequestList);
        break;
      // 棚卸調査表
      case PageId.PmInventoriesResearchList:
        pageIds.push(PageId.PmInventoriesResearchList);
        break;
      default:
        pageIds.push(pageId);
        break;
    }

    return pageIds;
  }

  /**
   * 出力状況に「全て表示」の追加
   * @param outputStatus 出力状況
   * @returns
   */
  addStatusDispAll(outputStatus: Array<{ value: string; text: string; }>): Array<{ value: string; text: string; }> {
    let statuses = _cloneDeep(outputStatus);
    statuses.unshift({ 'value': 'all', 'text': '全て表示' });
    statuses = _uniqBy(statuses, 'value');
    return statuses;
  }

}



// WEBPACK FOOTER //
// ./src/app/page/report-output-status-page/report-output-status-page.service.ts